import { useEffect, useRef, useState } from 'react'
import { usePageVisible } from '@/components/brand/useLoopsAllowed'
import { eventPhase, msUntilPhaseChange, type EventPhase } from './rules'

/**
 * The event's phase (`eventPhase`), kept current: a timer re-renders the caller at its next boundary (the start, or a
 * millisecond past the end), so an open watch screen turns from warming up to a quiet stretch, then to all finished,
 * without anything else changing. A boundary further off than a timer holds is waited for in steps. Timers don't run
 * while a phone sleeps (or a background tab is throttled), so the page coming back on screen reads the clock again: a
 * phone locked through the start doesn't wake to "Warming up".
 */
export function useEventPhase(startsAt: string | null | undefined, endsAt: string | null | undefined): EventPhase {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const wait = msUntilPhaseChange(startsAt, endsAt, now)
    if (wait === null) return
    const timer = setTimeout(() => setNow(Date.now()), wait)
    return () => clearTimeout(timer)
  }, [startsAt, endsAt, now])

  const visible = usePageVisible()
  const wasVisible = useRef(visible)
  useEffect(() => {
    const woke = visible && !wasVisible.current
    wasVisible.current = visible
    if (woke) setNow(Date.now())
  }, [visible])

  return eventPhase(startsAt, endsAt, now)
}
